import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Mail, Phone, MapPin, ArrowRight } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Reveal, Eyebrow } from "./shared";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initial = { first_name: "", last_name: "", email: "", phone: "", company: "", message: "" };

export const Contact = () => {
  const [form, setForm] = useState(initial);
  const [loading, setLoading] = useState(false);

  const onChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.first_name || !form.last_name || !form.email || !form.message) {
      toast.error("Please complete the required fields.");
      return;
    }
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/contact`, form);
      toast.success(data.message || "Thank you for reaching out.");
      setForm(initial);
    } catch (err) {
      toast.error("Something went wrong. Please try again or email us directly.");
    } finally {
      setLoading(false);
    }
  };

  const fieldCls =
    "bg-white border-[#D8D8D8] rounded-sm h-12 text-[#2B2B2B] focus-visible:ring-1 focus-visible:ring-[#3B2C82] focus-visible:border-[#3B2C82]";

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="relative py-28 lg:py-40"
      style={{ backgroundColor: "#F2F1EF" }}
    >
      <div className="max-w-[1400px] mx-auto px-6 lg:px-12 grid grid-cols-1 lg:grid-cols-12 gap-14 lg:gap-20">
        {/* Left: details */}
        <div className="lg:col-span-5">
          <Reveal><Eyebrow>Contact</Eyebrow></Reveal>
          <Reveal delay={0.05}>
            <h2 className="font-bold leading-[1.08] text-3xl sm:text-4xl lg:text-5xl" style={{ color: "#2B2B2B" }}>
              Let’s Build Something <span style={{ color: "#3B2C82" }}>Meaningful</span>
            </h2>
          </Reveal>
          <Reveal delay={0.1} as="p" className="mt-7 max-w-md text-base lg:text-lg leading-relaxed" style={{ color: "#8C8C8C" }}>
            Whether you’re exploring new opportunities, looking to establish your presence within
            DNHDD or interested in partnering with Fortim, we’d welcome the opportunity to start a
            conversation.
          </Reveal>

          <div className="mt-12 space-y-6">
            {[
              { Icon: Mail, label: "Email", value: "info@fortim.in", href: "mailto:info@fortim.in", testid: "contact-email" },
              { Icon: Phone, label: "Phone", value: "+91 76239 99924", href: "tel:+917623999924", testid: "contact-phone" },
              { Icon: MapPin, label: "Location", value: "1st Floor, M/s Naraina S. Fugro, Opposite Diu Court, Diu - 362520, Dadra Nagar Haveli and Daman and Diu", testid: "contact-location" },
            ].map(({ Icon, label, value, href, testid }) => (
              <Reveal key={label} delay={0.12} className="flex items-start gap-4">
                <div className="w-11 h-11 flex-shrink-0 flex items-center justify-center rounded-sm" style={{ border: "1px solid #D8D8D8", color: "#3B2C82" }}>
                  <Icon size={18} strokeWidth={1.6} />
                </div>
                <div>
                  <p className="eyebrow text-[10px] mb-1" style={{ color: "#8C8C8C" }}>{label}</p>
                  {href ? (
                    <a data-testid={testid} href={href} className="link-underline text-base font-medium" style={{ color: "#2B2B2B" }}>{value}</a>
                  ) : (
                    <p data-testid={testid} className="text-base font-medium max-w-xs" style={{ color: "#2B2B2B" }}>{value}</p>
                  )}
                </div>
              </Reveal>
            ))}
          </div>
        </div>

        {/* Right: form */}
        <div className="lg:col-span-7">
          <Reveal>
            <form
              data-testid="contact-form"
              onSubmit={onSubmit}
              className="p-7 lg:p-10 rounded-sm"
              style={{ backgroundColor: "#FAF9F7", border: "1px solid #D8D8D8" }}
            >
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="first_name" className="text-xs font-medium" style={{ color: "#8C8C8C" }}>First name *</Label>
                  <Input data-testid="contact-first-name" id="first_name" name="first_name" value={form.first_name} onChange={onChange} className={fieldCls} placeholder="Jane" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="last_name" className="text-xs font-medium" style={{ color: "#8C8C8C" }}>Last name *</Label>
                  <Input data-testid="contact-last-name" id="last_name" name="last_name" value={form.last_name} onChange={onChange} className={fieldCls} placeholder="Doe" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="email" className="text-xs font-medium" style={{ color: "#8C8C8C" }}>Email *</Label>
                  <Input data-testid="contact-email-input" id="email" name="email" type="email" value={form.email} onChange={onChange} className={fieldCls} placeholder="jane@company.com" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="phone" className="text-xs font-medium" style={{ color: "#8C8C8C" }}>Phone</Label>
                  <Input data-testid="contact-phone-input" id="phone" name="phone" value={form.phone} onChange={onChange} className={fieldCls} placeholder="+91 XXXXX XXXXX" />
                </div>
                <div className="space-y-2 sm:col-span-2">
                  <Label htmlFor="company" className="text-xs font-medium" style={{ color: "#8C8C8C" }}>Company name</Label>
                  <Input data-testid="contact-company" id="company" name="company" value={form.company} onChange={onChange} className={fieldCls} placeholder="Company Pvt. Ltd." />
                </div>
                <div className="space-y-2 sm:col-span-2">
                  <Label htmlFor="message" className="text-xs font-medium" style={{ color: "#8C8C8C" }}>Message *</Label>
                  <Textarea data-testid="contact-message" id="message" name="message" value={form.message} onChange={onChange} rows={5} className="bg-white border-[#D8D8D8] rounded-sm text-[#2B2B2B] focus-visible:ring-1 focus-visible:ring-[#3B2C82] focus-visible:border-[#3B2C82] resize-none" placeholder="Tell us a little about what you're exploring." />
                </div>
              </div>

              <button
                data-testid="contact-submit"
                type="submit"
                disabled={loading}
                className="group mt-8 inline-flex items-center justify-center gap-2 px-8 py-4 rounded-full text-sm font-semibold text-white transition-colors duration-300 disabled:opacity-60"
                style={{ backgroundColor: "#3B2C82" }}
                onMouseEnter={(e) => !loading && (e.currentTarget.style.backgroundColor = "#6B5CA5")}
                onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "#3B2C82")}
              >
                {loading ? "Sending..." : "Submit"}
                {!loading && <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />}
              </button>
            </form>
          </Reveal>
        </div>
      </div>
    </section>
  );
};
